import { useLanguage } from '../../../hooks/useLanguage';

export default function SolutionNew() {
  const { language } = useLanguage();

  const content = {
    de: {
      label: 'Die Lösung',
      headline: 'TELIQone automatisiert Ihre Telco-Prozesse end-to-end',
      subtext: 'Unsere KI-Plattform übernimmt repetitive Aufgaben, beschleunigt Workflows und senkt Ihre Betriebskosten — ohne manuelle Eingriffe.',
      modules: [
        {
          icon: 'ri-mail-line',
          title: 'Ticket-Klassifizierung',
          description: 'KI analysiert und routet Support-Tickets automatisch an die richtige Abteilung.'
        },
        {
          icon: 'ri-file-text-line',
          title: 'Rechnungsverarbeitung',
          description: 'Automatische Extraktion, Validierung und Weiterleitung von Rechnungsdaten.'
        },
        {
          icon: 'ri-customer-service-line',
          title: 'Kundenanfragen',
          description: '24/7 automatische Beantwortung häufiger Fragen via Chat, E-Mail oder Telefon.'
        },
        {
          icon: 'ri-database-line',
          title: 'Datenextraktion',
          description: 'Intelligente Erfassung strukturierter Daten aus unstrukturierten Dokumenten.'
        },
        {
          icon: 'ri-flow-chart',
          title: 'Workflow-Orchestrierung',
          description: 'Nahtlose Integration in bestehende Systeme und automatische Prozesssteuerung.'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Compliance & Audit',
          description: 'DSGVO-konforme Verarbeitung mit vollständiger Nachvollziehbarkeit aller Schritte.'
        },
        {
          icon: 'ri-bar-chart-box-line',
          title: 'Analytics & Reporting',
          description: 'Echtzeit-Einblicke in Prozesseffizienz, Kosteneinsparungen und Qualitätsmetriken.'
        }
      ],
      extensionsTitle: 'Erweiterungen',
      extensions: [
        'CRM-Integration (Salesforce, HubSpot)',
        'ERP-Anbindung (SAP, Oracle)',
        'Ticketing-Systeme (Jira, ServiceNow)',
        'Telefonie-Integration (VoIP, SIP)',
        'Custom APIs & Webhooks'
      ]
    },
    en: {
      label: 'The Solution',
      headline: 'TELIQone automates your telco processes end-to-end',
      subtext: 'Our AI platform takes over repetitive tasks, accelerates workflows, and reduces your operating costs — without manual intervention.',
      modules: [
        {
          icon: 'ri-mail-line',
          title: 'Ticket Classification',
          description: 'AI analyzes and routes support tickets automatically to the right department.'
        },
        {
          icon: 'ri-file-text-line',
          title: 'Invoice Processing',
          description: 'Automatic extraction, validation, and forwarding of invoice data.'
        },
        {
          icon: 'ri-customer-service-line',
          title: 'Customer Inquiries',
          description: '24/7 automatic answering of frequent questions via chat, email, or phone.'
        },
        {
          icon: 'ri-database-line',
          title: 'Data Extraction',
          description: 'Intelligent capture of structured data from unstructured documents.'
        },
        {
          icon: 'ri-flow-chart',
          title: 'Workflow Orchestration',
          description: 'Seamless integration into existing systems and automatic process control.'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Compliance & Audit',
          description: 'GDPR-compliant processing with full traceability of all steps.'
        },
        {
          icon: 'ri-bar-chart-box-line',
          title: 'Analytics & Reporting',
          description: 'Real-time insights into process efficiency, cost savings, and quality metrics.'
        }
      ],
      extensionsTitle: 'Extensions',
      extensions: [
        'CRM Integration (Salesforce, HubSpot)',
        'ERP Connection (SAP, Oracle)',
        'Ticketing Systems (Jira, ServiceNow)',
        'Telephony Integration (VoIP, SIP)',
        'Custom APIs & Webhooks'
      ]
    }
  };

  const t = content[language];

  return (
    <section id="solution" className="relative py-20 sm:py-28 bg-black">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12 sm:mb-16 fade-in-section">
          <div className="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full mb-4">
            <span className="text-blue-400 text-xs font-semibold uppercase tracking-widest">{t.label}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
            {t.headline}
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            {t.subtext}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {t.modules.map((module, i) => (
            <div
              key={i}
              className="fade-in-section bg-white/5 border border-white/10 rounded-xl p-6 hover:border-blue-500/40 transition-all group"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-blue-500/10 rounded-lg mb-4 group-hover:bg-blue-500/20 transition-all">
                <i className={`${module.icon} text-2xl text-blue-500`}></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{module.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{module.description}</p>
            </div>
          ))}
        </div>

        <div className="fade-in-section bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8">
          <h3 className="text-xl font-bold text-white mb-4">{t.extensionsTitle}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {t.extensions.map((ext, i) => (
              <div key={i} className="flex items-center gap-2">
                <i className="ri-check-line text-blue-500"></i>
                <span className="text-sm text-white/70">{ext}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}